import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { Users, Search as SearchIcon, Briefcase, Building2, RefreshCcw, Download, LogIn, Activity, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const EVENT_BADGE = {
  user_login: { className: "bg-slate-100 text-slate-600", label: "Login" },
  search_performed: { className: "bg-blue-100 text-blue-700", label: "Search" },
  deep_analysis_run: { className: "bg-teal-100 text-teal-700", label: "Deep Analysis" },
  work_order_submitted: { className: "bg-lime-100 text-lime-700", label: "Work Order" },
  work_order_status_changed: { className: "bg-amber-100 text-amber-700", label: "WO Status" },
  user_invited: { className: "bg-blue-100 text-blue-700", label: "Invite" },
  demo_invite_sent: { className: "bg-amber-100 text-amber-700", label: "Demo Invite" },
  org_created: { className: "bg-slate-100 text-slate-600", label: "Org Created" },
  demo_to_paid_upgrade: { className: "bg-lime-100 text-lime-700", label: "Upgrade" },
};

const PIE_COLORS = ['#022c22', '#14532d', '#166534', '#15803d', '#65a30d', '#84cc16', '#0d9488', '#14b8a6', '#94a3b8'];

function EventBadge({ type }) {
  const config = EVENT_BADGE[type] || { className: "bg-slate-100 text-slate-600", label: type };
  return <Badge className={`${config.className} border-0 text-xs font-accent`}>{config.label}</Badge>;
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <Card className="rounded-xl border bg-white shadow-sm">
      <CardContent className="p-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center"><Icon className="w-5 h-5 text-slate-600" /></div>
          <span className="text-xs font-mono uppercase tracking-widest text-slate-400">{label}</span>
        </div>
        <span className="text-3xl font-accent font-bold text-evergreen-950">{typeof value === "number" ? value.toLocaleString() : value}</span>
      </CardContent>
    </Card>
  );
}

function formatMeta(log) {
  const m = log.metadata || {};
  switch (log.event_type) {
    case "search_performed": return `Searched — ${m.pool_count ?? 0} candidates found`;
    case "deep_analysis_run": return `Scored ${m.candidates_scored ?? 0} candidates (top: ${m.top_score ?? "—"})`;
    case "work_order_submitted": return `${m.type === "job_promotion" ? "Job Promo" : "Outreach"} — ${m.candidate_count ?? 0} candidates, ${m.priority ?? "standard"}`;
    case "work_order_status_changed": return `${m.old_status} → ${m.new_status}`;
    case "user_invited": return `Invited ${m.invited_email}`;
    case "demo_invite_sent": return `Demo invite to ${m.invited_email}`;
    case "org_created": return `Created org: ${m.org_name}${m.is_demo ? " (demo)" : ""}`;
    case "demo_to_paid_upgrade": return `Upgraded ${m.org_name} to paid`;
    default: return "";
  }
}

export default function PlatformAnalytics() {
  const { api } = useAuth();
  const [rollup, setRollup] = useState(null);
  const [timeRange, setTimeRange] = useState("daily");
  const [platformStats, setPlatformStats] = useState(null);
  const [funnelData, setFunnelData] = useState([]);
  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsPage, setLogsPage] = useState(0);
  const [logFilter, setLogFilter] = useState({ event_type: "", org_id: "" });
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadRollup = useCallback(async (range) => {
    try {
      const r = range || timeRange;
      const res = await api.get(`/admin/event-logs/daily-rollup?range=${r}`);
      setRollup(res.data);
    } catch (e) { console.error(e); }
  }, [api, timeRange]);

  const loadPlatformStats = useCallback(async () => {
    try {
      const res = await api.get("/admin/analytics");
      setPlatformStats(res.data);
    } catch (e) { console.error(e); }
  }, [api]);

  const loadLogs = useCallback(async (page = 0) => {
    try {
      const params = new URLSearchParams({ limit: "50", offset: String(page * 50) });
      if (logFilter.event_type) params.set("event_type", logFilter.event_type);
      if (logFilter.org_id) params.set("org_id", logFilter.org_id);
      const res = await api.get(`/admin/event-logs?${params}`);
      setLogs(res.data.logs);
      setLogsTotal(res.data.total);
    } catch (e) { console.error(e); }
  }, [api, logFilter]);

  useEffect(() => {
    Promise.all([loadRollup(), loadPlatformStats(), loadLogs(0), api.get("/admin/clients").then(r => setOrgs(r.data)), api.get("/admin/conversion-funnel").then(r => setFunnelData(r.data)).catch(() => {})]).finally(() => setLoading(false));
    const interval = setInterval(loadRollup, 300000); // 5 min refresh
    return () => clearInterval(interval);
  }, [loadRollup, loadPlatformStats, loadLogs, api]);

  useEffect(() => { loadLogs(logsPage); }, [logsPage, loadLogs]);

  const handleExportCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (logFilter.event_type) params.set("event_type", logFilter.event_type);
      if (logFilter.org_id) params.set("org_id", logFilter.org_id);
      const res = await api.get(`/admin/event-logs/export-csv?${params}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = "event_logs.csv"; a.click();
      toast.success("CSV exported");
    } catch { toast.error("Export failed"); }
  };

  const RANGE_LABELS = { daily: "24 Hours", weekly: "7 Days", monthly: "30 Days", quarterly: "90 Days" };
  const activityData = rollup ? Object.entries(rollup.activity || {}).sort().map(([key, count]) => {
    const label = rollup.hours <= 24 ? key.slice(11, 16) : key.slice(5, 10);
    return { label, count };
  }) : [];
  const pieData = rollup ? Object.entries(rollup.event_type_breakdown || {}).map(([name, value]) => ({ name: (EVENT_BADGE[name]?.label || name), value })) : [];

  if (loading) return <div className="max-w-7xl mx-auto px-4 py-8"><div className="grid grid-cols-4 gap-6">{[1,2,3,4].map(i => <div key={i} className="h-32 bg-white rounded-xl border animate-pulse" />)}</div></div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="platform-analytics">
      <h1 className="text-2xl font-heading text-slate-900 mb-2">Activity & Analytics</h1>
      <p className="text-sm text-slate-500 mb-6">Platform activity, event logs, and candidate database stats</p>

      <Tabs defaultValue="daily">
        <TabsList className="mb-6">
          <TabsTrigger value="daily" data-testid="tab-daily">Activity</TabsTrigger>
          <TabsTrigger value="login-log" data-testid="tab-login-log">Login Log</TabsTrigger>
          <TabsTrigger value="activity" data-testid="tab-activity">Activity Feed</TabsTrigger>
          <TabsTrigger value="platform" data-testid="tab-platform">Platform Stats</TabsTrigger>
          <TabsTrigger value="funnel" data-testid="tab-funnel">Conversion Funnel</TabsTrigger>
        </TabsList>

        {/* TAB 1: Activity Roll-up */}
        <TabsContent value="daily">
          <div className="flex items-center justify-between mb-4">
            <div className="flex bg-white rounded-lg border p-1">
              {(["daily", "weekly", "monthly", "quarterly"]).map(r => (
                <button key={r} onClick={() => { setTimeRange(r); loadRollup(r); }}
                  className={`px-3 py-1.5 text-xs font-accent rounded-md transition-colors ${timeRange === r ? "bg-lime-50 text-lime-700 font-medium" : "text-slate-500 hover:text-slate-700"}`}
                  data-testid={`range-${r}`}>
                  {RANGE_LABELS[r]}
                </button>
              ))}
            </div>
            <Button variant="outline" size="sm" onClick={() => loadRollup()} data-testid="refresh-rollup"><RefreshCcw className="w-4 h-4 mr-1" /> Refresh</Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <StatCard icon={LogIn} label={`Logins (${RANGE_LABELS[timeRange]})`} value={rollup?.logins ?? 0} />
            <StatCard icon={SearchIcon} label={`Searches (${RANGE_LABELS[timeRange]})`} value={rollup?.searches ?? 0} />
            <StatCard icon={Activity} label={`Deep Analyses (${RANGE_LABELS[timeRange]})`} value={rollup?.analyses ?? 0} />
            <StatCard icon={Briefcase} label={`Work Orders (${RANGE_LABELS[timeRange]})`} value={rollup?.work_orders ?? 0} />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="rounded-xl border"><CardContent className="p-6">
              <h3 className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-4">Activity ({RANGE_LABELS[timeRange]})</h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={activityData}><CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" /><XAxis dataKey="label" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 11 }} /><Tooltip /><Bar dataKey="count" fill="#65a30d" radius={[4, 4, 0, 0]} /></BarChart>
              </ResponsiveContainer>
            </CardContent></Card>
            <Card className="rounded-xl border"><CardContent className="p-6">
              <h3 className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-4">Event Type Breakdown</h3>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart><Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={2} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false} style={{ fontSize: 11 }}>
                  {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie><Tooltip /></PieChart>
              </ResponsiveContainer>
            </CardContent></Card>
          </div>
        </TabsContent>

        {/* TAB 2: Login Log */}
        <TabsContent value="login-log">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <Select value={logFilter.org_id || "all"} onValueChange={v => { setLogFilter(p => ({ ...p, org_id: v === "all" ? "" : v })); setLogsPage(0); }}>
              <SelectTrigger className="w-48"><SelectValue placeholder="All Orgs" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Organizations</SelectItem>{orgs.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={logFilter.event_type || "all"} onValueChange={v => { setLogFilter(p => ({ ...p, event_type: v === "all" ? "" : v })); setLogsPage(0); }}>
              <SelectTrigger className="w-48"><SelectValue placeholder="All Events" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Events</SelectItem>{Object.keys(EVENT_BADGE).map(e => <SelectItem key={e} value={e}>{EVENT_BADGE[e].label}</SelectItem>)}</SelectContent>
            </Select>
            <div className="flex-1" />
            <Button variant="outline" size="sm" onClick={handleExportCSV} data-testid="export-csv-btn"><Download className="w-4 h-4 mr-1" /> Export CSV</Button>
          </div>
          <div className="bg-white rounded-xl border overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 text-left">
                <th className="px-4 py-3 text-xs font-mono uppercase tracking-widest text-slate-400">Timestamp</th>
                <th className="px-4 py-3 text-xs font-mono uppercase tracking-widest text-slate-400">User</th>
                <th className="px-4 py-3 text-xs font-mono uppercase tracking-widest text-slate-400">Organization</th>
                <th className="px-4 py-3 text-xs font-mono uppercase tracking-widest text-slate-400">Event</th>
              </tr></thead>
              <tbody>
                {logs.map(l => (
                  <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3 text-xs font-mono text-slate-500">{new Date(l.created_at).toLocaleString()}</td>
                    <td className="px-4 py-3 text-slate-900">{l.user_name || "—"}</td>
                    <td className="px-4 py-3"><span className="text-slate-600">{l.org_name || "—"}</span>{l.is_demo && <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px] ml-2">Demo</Badge>}</td>
                    <td className="px-4 py-3"><EventBadge type={l.event_type} /></td>
                  </tr>
                ))}
                {logs.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-400">No events found</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between mt-4">
            <span className="text-sm text-slate-500">{logsTotal} events total</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={logsPage === 0} onClick={() => setLogsPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
              <Button variant="outline" size="sm" disabled={(logsPage + 1) * 50 >= logsTotal} onClick={() => setLogsPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
        </TabsContent>

        {/* TAB 3: Activity Feed */}
        <TabsContent value="activity">
          <div className="space-y-2">
            {logs.map((l, idx) => (
              <div key={l.id} className="flex items-start gap-4 p-3 bg-white rounded-lg border border-slate-100 animate-slideUp" style={{ animationDelay: `${idx * 0.02}s` }}>
                <span className="text-xs font-mono text-slate-400 whitespace-nowrap mt-0.5">{new Date(l.created_at).toLocaleTimeString()}</span>
                <EventBadge type={l.event_type} />
                <div className="flex-1 min-w-0">
                  <span className="text-sm text-slate-900 font-medium">{l.user_name}</span>
                  {l.org_name && <span className="text-sm text-slate-400 mx-1">at</span>}
                  <span className="text-sm text-slate-600">{l.org_name}</span>
                  {l.is_demo && <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px] ml-1">Demo</Badge>}
                  <p className="text-xs text-slate-500 mt-0.5">{formatMeta(l)}</p>
                </div>
              </div>
            ))}
            {logs.length === 0 && <Card className="border-dashed"><CardContent className="p-8 text-center"><p className="text-sm text-slate-400">No activity yet</p></CardContent></Card>}
          </div>
          <div className="flex items-center justify-between mt-4">
            <span className="text-sm text-slate-500">{logsTotal} events total</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={logsPage === 0} onClick={() => setLogsPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
              <Button variant="outline" size="sm" disabled={(logsPage + 1) * 50 >= logsTotal} onClick={() => setLogsPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
        </TabsContent>

        {/* TAB 4: Platform Stats */}
        <TabsContent value="platform">
          {platformStats && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <StatCard icon={Users} label="Total Candidates" value={platformStats.total_candidates} />
                <StatCard icon={SearchIcon} label="Role Searches" value={platformStats.total_searches} />
                <StatCard icon={Briefcase} label="Work Orders" value={platformStats.total_work_orders} />
                <StatCard icon={Building2} label="Client Orgs" value={platformStats.total_clients} />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                {[["Seniority Distribution", platformStats.seniority_distribution, "#022c22"],
                  ["Functional Areas", platformStats.functional_area_distribution, "#65a30d"],
                  ["Geography", platformStats.geography_distribution, "#0d9488"],
                  ["Therapeutic Areas", platformStats.therapeutic_area_distribution, "#14532d"]
                ].map(([title, data, color]) => {
                  const chartData = Object.entries(data || {}).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([name, value]) => ({ name: name.length > 18 ? name.slice(0, 16) + "..." : name, value }));
                  return (
                    <Card key={title} className="rounded-xl border"><CardContent className="p-6">
                      <h3 className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-4">{title}</h3>
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={chartData} layout="vertical" margin={{ left: 0, right: 16, top: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" /><XAxis type="number" tick={{ fontSize: 11 }} /><YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={120} /><Tooltip /><Bar dataKey="value" fill={color} radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent></Card>
                  );
                })}
              </div>
            </>
          )}
        </TabsContent>

        {/* TAB 5: Conversion Funnel */}
        <TabsContent value="funnel">
          <p className="text-sm text-slate-500 mb-6">Search-to-Work Order conversion tracking by client organization.</p>
          <div className="bg-white rounded-xl border overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 text-left">
                <th className="px-4 py-3 text-xs font-mono uppercase tracking-widest text-slate-400">Client</th>
                <th className="px-3 py-3 text-xs font-mono uppercase tracking-widest text-slate-400 text-center">Searches</th>
                <th className="px-3 py-3 text-xs font-mono uppercase tracking-widest text-slate-400 text-center">Analyzed</th>
                <th className="px-3 py-3 text-xs font-mono uppercase tracking-widest text-slate-400 text-center">Scored</th>
                <th className="px-3 py-3 text-xs font-mono uppercase tracking-widest text-slate-400 text-center">Selected</th>
                <th className="px-3 py-3 text-xs font-mono uppercase tracking-widest text-slate-400 text-center">WOs</th>
                <th className="px-3 py-3 text-xs font-mono uppercase tracking-widest text-slate-400 text-center">Interested</th>
                <th className="px-3 py-3 text-xs font-mono uppercase tracking-widest text-slate-400 text-center">Consented</th>
                <th className="px-4 py-3 text-xs font-mono uppercase tracking-widest text-slate-400">Top Criteria</th>
              </tr></thead>
              <tbody>
                {funnelData.map(f => (
                  <tr key={f.org_id} className="border-t border-slate-100 hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3"><span className="font-medium text-slate-900">{f.org_name}</span>{f.is_demo && <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px] ml-2">Demo</Badge>}</td>
                    <td className="px-3 py-3 text-center font-accent font-bold text-slate-900">{f.total_searches}</td>
                    <td className="px-3 py-3 text-center font-accent text-slate-600">{f.searches_with_analysis}</td>
                    <td className="px-3 py-3 text-center font-accent text-slate-600">{f.total_candidates_scored}</td>
                    <td className="px-3 py-3 text-center font-accent text-slate-600">{f.total_candidates_selected}</td>
                    <td className="px-3 py-3 text-center font-accent font-bold text-evergreen-950">{f.total_work_orders}</td>
                    <td className="px-3 py-3 text-center font-accent font-bold text-lime-700">{f.total_interested}</td>
                    <td className="px-3 py-3 text-center font-accent font-bold text-teal-700">{f.total_consented}</td>
                    <td className="px-4 py-3"><div className="flex flex-wrap gap-1">{f.top_criteria?.slice(0, 3).map(c => <Badge key={c.name} variant="secondary" className="text-[10px]">{c.name}</Badge>)}</div></td>
                  </tr>
                ))}
                {funnelData.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-400">No conversion data yet</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
